import { useMemo, useState } from 'react';
import { MOTIVATION_DEF_LIST, ORIGIN_DEF_LIST, PLAYER_NAME_SUGGESTIONS } from '@core/content';
import { makeSeed } from '@core/rng';
import type { NewLifeOptions } from '@core/newgame';
import './Screens.css';

/* ------------------------------------------------------------------ *
 * Title
 * ------------------------------------------------------------------ */

interface TitleProps {
  hasAutosave: boolean;
  onNew: () => void;
  onContinue: () => void;
  onSaves: () => void;
}

export function TitleScreen({ hasAutosave, onNew, onContinue, onSaves }: TitleProps) {
  return (
    <main className="screen screen--title">
      <div className="title">
        <p className="eyebrow title__eyebrow">A life simulation</p>
        <h1 className="title__word">A LIFE</h1>
        <p className="title__tagline">
          Sixty years of choices, opportunity costs and things you meant to get round to.
          <br />
          There is no winning score. There is only the life you end up with.
        </p>
        <div className="title__actions">
          {hasAutosave && (
            <button type="button" className="btn btn--primary" onClick={onContinue}>
              Continue
            </button>
          )}
          <button
            type="button"
            className={hasAutosave ? 'btn' : 'btn btn--primary'}
            onClick={onNew}
          >
            Begin a new life
          </button>
          <button type="button" className="btn btn--ghost" onClick={onSaves}>
            Saved lives
          </button>
        </div>
        <ul className="title__pillars">
          <li>
            <strong>Every choice costs something</strong>
            <span>Time, money, energy or the people around you. Usually more than one.</span>
          </li>
          <li>
            <strong>Consequences arrive late</strong>
            <span>The promotion you took at 29 is why you are exhausted at 36.</span>
          </li>
          <li>
            <strong>Intending is not doing</strong>
            <span>Say “later” often enough and the decision gets made for you.</span>
          </li>
        </ul>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------------ *
 * Setup
 * ------------------------------------------------------------------ */

interface SetupProps {
  onStart: (options: NewLifeOptions) => void;
  onBack: () => void;
}

export function SetupScreen({ onStart, onBack }: SetupProps) {
  const suggestion = useMemo(
    () => PLAYER_NAME_SUGGESTIONS[Math.floor(Math.random() * PLAYER_NAME_SUGGESTIONS.length)] ?? 'Avery Lane',
    [],
  );
  const [name, setName] = useState(suggestion);
  const [originId, setOriginId] = useState(ORIGIN_DEF_LIST[0]?.id ?? 'origin.steady');
  const [motivationId, setMotivationId] = useState(MOTIVATION_DEF_LIST[0]?.id ?? 'motive.security');
  const [seed, setSeed] = useState(makeSeed());

  return (
    <main className="screen screen--setup">
      <div className="setup">
        <header className="setup__head">
          <button type="button" className="btn btn--ghost btn--small" onClick={onBack}>
            ← Back
          </button>
          <h1 className="setup__title">Before we start</h1>
          <p className="setup__lead">
            Three things you do not get to choose later: where you came from, what you are chasing,
            and your name.
          </p>
        </header>

        <section className="setup__field">
          <label className="setup__label" htmlFor="name">
            Name
          </label>
          <input
            id="name"
            className="input"
            value={name}
            maxLength={40}
            onChange={(event) => setName(event.target.value)}
          />
        </section>

        <section className="setup__field">
          <span className="setup__label">Where you started</span>
          <p className="setup__note">
            Circumstance matters, especially early. None of these is strictly better than the others.
          </p>
          <div className="options">
            {ORIGIN_DEF_LIST.map((origin) => (
              <button
                key={origin.id}
                type="button"
                className={`option${originId === origin.id ? ' option--on' : ''}`}
                onClick={() => setOriginId(origin.id)}
                aria-pressed={originId === origin.id}
              >
                <span className="option__label">{origin.label}</span>
                <span className="option__blurb">{origin.blurb}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="setup__field">
          <span className="setup__label">What you are after</span>
          <p className="setup__note">
            A statement of intent, not a class. Plenty of lives end up somewhere else entirely.
          </p>
          <div className="options">
            {MOTIVATION_DEF_LIST.map((motivation) => (
              <button
                key={motivation.id}
                type="button"
                className={`option${motivationId === motivation.id ? ' option--on' : ''}`}
                onClick={() => setMotivationId(motivation.id)}
                aria-pressed={motivationId === motivation.id}
              >
                <span className="option__label">{motivation.label}</span>
                <span className="option__blurb">{motivation.blurb}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="setup__field setup__field--row">
          <div>
            <label className="setup__label" htmlFor="seed">
              Seed
            </label>
            <p className="setup__note">The same seed and the same choices give the same life.</p>
          </div>
          <div className="setup__seed">
            <input
              id="seed"
              className="input input--mono"
              value={seed}
              maxLength={24}
              onChange={(event) => setSeed(event.target.value.toUpperCase())}
            />
            <button type="button" className="btn btn--small btn--ghost" onClick={() => setSeed(makeSeed())}>
              Reroll
            </button>
          </div>
        </section>

        <button
          type="button"
          className="btn btn--primary btn--wide"
          onClick={() => onStart({ name: name.trim() || suggestion, originId, motivationId, seed: seed.trim() || makeSeed() })}
        >
          Begin at eighteen
        </button>
      </div>
    </main>
  );
}
